import React, { useState } from "react";
import Layout from "../../layout/Layout";
import {
  Button,
  Card,
  Badge,
  Select,
  Modal,
  Form,
  Input,
  Table,
  Space,
  Typography,
  Statistic,
  Row,
  Col,
  Tag,
  Popconfirm,
  // ColorPicker,
  // App
} from "antd";
import {
  PlusOutlined,
  SettingOutlined,
  MonitorOutlined,
  PrinterOutlined,
  EditOutlined,
  DeleteOutlined,
  PoweroffOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined
} from "@ant-design/icons";

const { Title, Paragraph } = Typography;
const { Option } = Select;

const StationManagement = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingStation, setEditingStation] = useState(null);
  const [form] = Form.useForm();
  // const { message } = App.useApp();

  // Mock data - in real app this would come from API
  const [stations, setStations] = useState([
    {
      id: '1',
      name: 'Grill Station',
      type: 'product',
      outputMethod: 'both',
      assignedKdsDevice: 'KDS-001',
      assignedPrinter: 'PRINTER-001',
      displayColor: '#EF4444',
      isActive: true,
      status: 'online'
    },
    {
      id: '2',
      name: 'Cold Station',
      type: 'product',
      outputMethod: 'kds',
      assignedKdsDevice: 'KDS-002',
      displayColor: '#3B82F6',
      isActive: true,
      status: 'online'
    },
    {
      id: '3',
      name: 'Expeditor',
      type: 'both',
      outputMethod: 'kds',
      assignedKdsDevice: 'KDS-003',
      displayColor: '#10B981',
      isActive: true,
      status: 'warning'
    },
    {
      id: '4',
      name: 'Beverage Station',
      type: 'service',
      outputMethod: 'printer',
      assignedPrinter: 'PRINTER-002',
      displayColor: '#F59E0B',
      isActive: false,
      status: 'offline'
    }
  ]);

  const mockKdsDevices = [
    { id: 'KDS-001', name: 'Grill KDS #1', available: false },
    { id: 'KDS-002', name: 'Cold KDS #1', available: false },
    { id: 'KDS-003', name: 'Expeditor KDS', available: false },
    { id: 'KDS-004', name: 'Prep KDS #1', available: true },
    { id: 'KDS-005', name: 'Backup KDS', available: true }
  ];

  const mockPrinters = [
    { id: 'PRINTER-001', name: 'Grill Printer', available: false },
    { id: 'PRINTER-002', name: 'Beverage Printer', available: false },
    { id: 'PRINTER-003', name: 'Backup Printer', available: true }
  ];

  const handleSubmit = (values) => {
    if (editingStation) {
      // Update existing station
      setStations(stations.map(s => s.id === editingStation.id ? {
        ...s,
        ...values,
        isActive: true,
        status: 'online'
      } : s));
      // message.success('Station updated successfully');
    } else {
      // Add new station
      const newStation = {
        id: Date.now().toString(),
        ...values,
        isActive: true,
        status: 'online'
      };
      setStations([...stations, newStation]);
      // message.success('Station added successfully');
    }
    
    resetForm();
  };

  const resetForm = () => {
    form.resetFields();
    setEditingStation(null);
    setIsModalVisible(false);
  };

  const handleEdit = (station) => {
    setEditingStation(station);
    form.setFieldsValue({
      name: station.name,
      type: station.type,
      outputMethod: station.outputMethod,
      assignedKdsDevice: station.assignedKdsDevice || undefined,
      assignedPrinter: station.assignedPrinter || undefined,
      displayColor: station.displayColor
    });
    setIsModalVisible(true);
  };

  const toggleStationStatus = (stationId) => {
    setStations(stations.map(s => s.id === stationId ? {
      ...s,
      isActive: !s.isActive,
      status: s.isActive ? 'offline' : 'online'
    } : s));
    // message.success('Station status updated');
  };

  const deleteStation = (stationId) => {
    setStations(stations.filter(s => s.id !== stationId));
    // message.success('Station deleted successfully');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'warning':
        return <WarningOutlined style={{ color: '#fa8c16' }} />;
      case 'offline':
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return <PoweroffOutlined style={{ color: '#d9d9d9' }} />;
    }
  };

  const getOutputMethodDisplay = (station) => {
    const icons = [];
    if (station.outputMethod === 'kds' || station.outputMethod === 'both') {
      icons.push(<MonitorOutlined key="kds" style={{ color: '#1890ff' }} />);
    }
    if (station.outputMethod === 'printer' || station.outputMethod === 'both') {
      icons.push(<PrinterOutlined key="printer" style={{ color: '#fa8c16' }} />);
    }
    return <Space>{icons}</Space>;
  };

  const columns = [
    {
      title: 'Station',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <div 
            style={{
              width: 16,
              height: 16,
              borderRadius: '50%',
              backgroundColor: record.displayColor,
              border: '2px solid white',
              boxShadow: '0 0 0 1px #d9d9d9'
            }}
          />
          <span style={{ fontWeight: 500 }}>{text}</span>
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag color="blue" style={{ textTransform: 'capitalize' }}>
          {type}
        </Tag>
      ),
    },
    {
      title: 'Output Method',
      dataIndex: 'outputMethod',
      key: 'outputMethod',
      render: (method, record) => (
        <Space>
          {getOutputMethodDisplay(record)}
          <span style={{ textTransform: 'capitalize' }}>{method}</span>
        </Space>
      ),
    },
    {
      title: 'Assigned Devices',
      key: 'devices',
      render: (_, record) => (
        <div>
          {record.assignedKdsDevice && (
            <div style={{ marginBottom: 4 }}>
              <Space size={4}>
                <MonitorOutlined style={{ color: '#1890ff', fontSize: 12 }} />
                <span style={{ fontSize: 12 }}>{record.assignedKdsDevice}</span>
              </Space>
            </div>
          )}
          {record.assignedPrinter && (
            <div>
              <Space size={4}>
                <PrinterOutlined style={{ color: '#fa8c16', fontSize: 12 }} />
                <span style={{ fontSize: 12 }}>{record.assignedPrinter}</span>
              </Space>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Space>
          {getStatusIcon(status)}
          <span style={{ 
            textTransform: 'capitalize',
            color: status === 'online' ? '#52c41a' : 
                   status === 'warning' ? '#fa8c16' : '#ff4d4f'
          }}>
            {status}
          </span>
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            type="text"
            size="small"
            icon={<PoweroffOutlined />}
            onClick={() => toggleStationStatus(record.id)}
            style={{ color: record.isActive ? '#ff4d4f' : '#52c41a' }}
          />
          <Popconfirm
            title="Delete Station"
            description="Are you sure you want to delete this station?"
            onConfirm={() => deleteStation(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              size="small"
              icon={<DeleteOutlined />}
              danger
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout>
      <div style={{ padding: "0 24px" }}>
        {/* Header */}
        <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <Title level={2}>Station Management</Title>
            <Paragraph>Configure output methods and device assignments for kitchen stations</Paragraph>
          </div>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => setIsModalVisible(true)}
          >
            Add Station
          </Button>
        </div>

        {/* Summary Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Stations"
                value={stations.length}
                prefix={<SettingOutlined style={{ color: '#2563EB' }} />}
              />
            </Card>
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Active Stations"
                value={stations.filter(s => s.isActive).length}
                valueStyle={{ color: '#52c41a' }}
                prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="KDS Assigned"
                value={stations.filter(s => s.outputMethod === 'kds' || s.outputMethod === 'both').length}
                valueStyle={{ color: '#2563EB' }}
                prefix={<MonitorOutlined style={{ color: '#2563EB' }} />}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Printer Assigned"
                value={stations.filter(s => s.outputMethod === 'printer' || s.outputMethod === 'both').length}
                valueStyle={{ color: '#fa8c16' }}
                prefix={<PrinterOutlined style={{ color: '#fa8c16' }} />}
              />
            </Card>
          </Col>
        </Row>

        {/* Stations Table */}
        <Card 
          title="Station Configuration"
          extra={<span style={{ color: '#666' }}>Manage output methods and device assignments for each kitchen station</span>}
        >
          <Table
            columns={columns}
            dataSource={stations}
            rowKey="id"
            pagination={false}
          />
        </Card>

        {/* Add/Edit Station Modal */}
        <Modal
          title={editingStation ? 'Edit Station' : 'Add New Station'}
          open={isModalVisible}
          onCancel={resetForm}
          footer={null}
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              type: 'product',
              outputMethod: 'kds',
              displayColor: '#3B82F6'
            }}
          >
            <Form.Item
              name="name"
              label="Station Name"
              rules={[{ required: true, message: 'Please enter station name' }]}
            >
              <Input placeholder="Enter station name" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="type"
                  label="Station Type"
                  rules={[{ required: true, message: 'Please select station type' }]}
                >
                  <Select placeholder="Select type">
                    <Option value="product">Product</Option>
                    <Option value="service">Service</Option>
                    <Option value="both">Both</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="outputMethod"
                  label="Output Method"
                  rules={[{ required: true, message: 'Please select output method' }]}
                >
                  <Select placeholder="Select output method">
                    <Option value="kds">KDS Only</Option>
                    <Option value="printer">Printer Only</Option>
                    <Option value="both">KDS + Printer</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.outputMethod !== currentValues.outputMethod}>
              {({ getFieldValue }) => {
                const outputMethod = getFieldValue('outputMethod');
                return (
                  <>
                    {(outputMethod === 'kds' || outputMethod === 'both') && (
                      <Form.Item
                        name="assignedKdsDevice"
                        label="Assigned KDS Device"
                      >
                        <Select placeholder="Select KDS device" allowClear>
                          {mockKdsDevices.filter(d => d.available || d.id === form.getFieldValue('assignedKdsDevice')).map((device) => (
                            <Option key={device.id} value={device.id}>
                              {device.name} {!device.available && '(Currently Assigned)'}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    )}

                    {(outputMethod === 'printer' || outputMethod === 'both') && (
                      <Form.Item
                        name="assignedPrinter"
                        label="Assigned Printer"
                      >
                        <Select placeholder="Select printer" allowClear>
                          {mockPrinters.filter(p => p.available || p.id === form.getFieldValue('assignedPrinter')).map((printer) => (
                            <Option key={printer.id} value={printer.id}>
                              {printer.name} {!printer.available && '(Currently Assigned)'}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    )}
                  </>
                );
              }}
            </Form.Item>

            <Form.Item
              name="displayColor"
              label="Display Color"
            >
              {/* <ColorPicker 
                showText 
                format="hex"
                disabledAlpha
              /> */}
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={resetForm}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit">
                  {editingStation ? 'Update Station' : 'Add Station'}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </Layout>
  );
};

export default StationManagement;
